import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import type { ActivityFeedInformerItemFindInterface } from '@zaoblako/shared/modules/notification/dtos/informer/activity-feed-informer-item-find.dto';
import type { ActivityFeedInformerQueryInterface } from '@zaoblako/shared/modules/notification/dtos/informer/activity-feed-informer-query.dto';
import { NotificationKindEnum } from '@zaoblako/shared/modules/notification/enums/notification.kind.enum';
import { NotificationSubjectEnum } from '@zaoblako/shared/modules/notification/enums/notification.subject.enum';
import { NotificationService } from '@zaoblako/web/core/services/notification/notification.service';
import { UnreadCountStorageService } from '@zaoblako/web/core/services/notification/unread-count-storage.service';
import { WebsocketNotificationService } from '@zaoblako/web/core/services/notification/websocket-notification.service';
import { NoDataComponent } from '@zaoblako/web/shared/components/no-data/no-data.component';
import { UserAvatarComponent } from '@zaoblako/web/shared/components/user-avatar/user-avatar.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ConcatDateParamsPipe } from '@zaoblako/web/shared/pipes/concat-date-params.pipe';
import { ConvertNotificationToLinkPipe } from '@zaoblako/web/shared/pipes/convert-notification-to-link.pipe';
import { NotificationTargetStatusEnum } from '@zaoblako/shared/modules/notification/enums/notification-target-status.enum';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { SharedModule } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { NoDataSize } from '../../no-data/constants/no-data-types.enum';

@Component({
  selector: 'crm-notification-task-list',
  standalone: true,
  imports: [
    CommonModule,
    BadgeModule,
    ButtonComponent,
    ConcatDateParamsPipe,
    ConvertNotificationToLinkPipe,
    NoDataComponent,
    OrderByPipe,
    OverlayPanelModule,
    ProgressSpinnerModule,
    RouterLink,
    SafeHtmlPipe,
    SharedModule,
    TagModule,
    UserAvatarComponent,
    TooltipModule,
    SpinnerComponent
  ],
  templateUrl: './notification-task-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationTaskListComponent implements OnInit, OnDestroy {
  public readonly informerType: NotificationKindEnum = NotificationKindEnum.TASK;
  @Input() public icon: string;
  @Input() public header: string;

  public queryParams: ActivityFeedInformerQueryInterface;
  public loading = false;
  public paginationLoading = false;
  public notifications: ActivityFeedInformerItemFindInterface[] = [];
  public paymentNotifications: ActivityFeedInformerItemFindInterface[] = [];
  public budgetNotifications: ActivityFeedInformerItemFindInterface[] = [];
  public plannedIncomeNotifications: ActivityFeedInformerItemFindInterface[] = [];
  public countItemsNotification: number;
  public unreadCount = 0;
  public NoDataSize = NoDataSize.M;

  public informerOpen = false;

  private newWSMessageSubscription: Subscription;
  private wsNotificationReadSubscription: Subscription;
  private unreadCountSubscription: Subscription;
  private readNotificationSubscription: Subscription;

  public get NotificationTargetStatusEnum() {
    return NotificationTargetStatusEnum;
  }

  public constructor(
    private readonly websocketNotificationService: WebsocketNotificationService,
    private readonly unreadCountStorageService: UnreadCountStorageService,
    private readonly notificationService: NotificationService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.queryParams = {
      limit: 20,
      offset: 0,
      kinds: [this.informerType]
    };
    this.newWSMessageSubscription = this.websocketNotificationService.getNewMessage().subscribe({
      next: (socketMessage) => {
        if (socketMessage.message.kind === this.informerType) {
          this.websocketNotificationService.showNotificationPopup(socketMessage.message);
          this.unreadCount++;
          this.cdRef.markForCheck();
        }
      },
      error: (err) => console.error(err),
      complete: () => console.log('complete')
    });

    this.wsNotificationReadSubscription = this.websocketNotificationService.socketNotificationRead$.subscribe(
      (notification) => {
        if (notification.kind === this.informerType) {
          this.unreadCount--;
          this.cdRef.markForCheck();
        }
      }
    );

    this.unreadCountSubscription = this.notificationService.unreadCount$.subscribe((unreadCount) => {
      this.unreadCount = unreadCount?.[this.informerType] ?? 0;
      this.cdRef.markForCheck();
    });

    this.readNotificationSubscription = this.notificationService.eventReadNotification.subscribe((informerType) => {
      if (this.informerType === informerType) {
        this.unreadCount--;
        this.cdRef.markForCheck();
      }
    });
  }

  public ngOnDestroy() {
    this.newWSMessageSubscription?.unsubscribe();
    this.wsNotificationReadSubscription?.unsubscribe();
    this.unreadCountSubscription?.unsubscribe();
    this.readNotificationSubscription?.unsubscribe();
  }

  public onInformerShow(): void {
    this.informerOpen = true;
    this.loadNotifications();
    this.cdRef.markForCheck();
  }

  public onInformerHide(): void {
    this.informerOpen = false;
    this.queryParams.offset = 0;
    this.cdRef.markForCheck();
  }

  public loadNotifications({ concat } = { concat: false }): void {
    if (!concat) {
      this.loading = true;
      this.cdRef.markForCheck();
    }

    this.notificationService.getUnreadNotificationCount().subscribe((unreadCount) => {
      this.notificationService.unreadCount$.next(unreadCount);
    });

    this.websocketNotificationService.findList(this.queryParams).subscribe({
      next: (notifications) => {
        this.budgetNotifications = concat
          ? this.budgetNotifications.concat(
              notifications.items.filter(
                (item) => item.lastNotification.subjectType === NotificationSubjectEnum.BUDGET_REQUEST
              )
            )
          : notifications.items.filter(
              (item) => item.lastNotification.subjectType === NotificationSubjectEnum.BUDGET_REQUEST
            );
        this.paymentNotifications = concat
          ? this.paymentNotifications.concat(
              notifications.items.filter((item) => item.lastNotification.subjectType === NotificationSubjectEnum.TASK)
            )
          : notifications.items.filter((item) => item.lastNotification.subjectType === NotificationSubjectEnum.TASK);
        this.plannedIncomeNotifications = concat
          ? this.plannedIncomeNotifications.concat(
              notifications.items.filter((item) => item.lastNotification.subjectType === NotificationSubjectEnum.PLANNED_INCOME)
            )
          : notifications.items.filter((item) => item.lastNotification.subjectType === NotificationSubjectEnum.PLANNED_INCOME);
        this.notifications = concat ? this.notifications.concat(notifications.items) : notifications.items;
        this.countItemsNotification = notifications.count;
        this.loading = false;
        this.paginationLoading = false;

        this.cdRef.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public read(notification: ActivityFeedInformerItemFindInterface): void {
    this.websocketNotificationService.readNotification({ notificationIds: notification.notificationIds }).subscribe({
      next: () => {
        if (notification.lastNotification.subjectType === NotificationSubjectEnum.BUDGET_REQUEST) {
          this.budgetNotifications.splice(this.budgetNotifications.indexOf(notification), 1);
        } else if (notification.lastNotification.subjectType === NotificationSubjectEnum.TASK) {
          this.paymentNotifications.splice(this.paymentNotifications.indexOf(notification), 1);
        } else if (notification.lastNotification.subjectType === NotificationSubjectEnum.PLANNED_INCOME) {
          this.plannedIncomeNotifications.splice(this.paymentNotifications.indexOf(notification), 1);
        }
        this.notifications.splice(this.notifications.indexOf(notification), 1);
        this.unreadCount -= notification.notificationIds?.length ?? 0;
        this.unreadCountStorageService.setParam({
          ...this.notificationService.unreadCount$.value,
          [this.informerType]: this.unreadCount
        });
        this.cdRef.markForCheck();
      }
    });
  }

  public onScrollEnd(target: HTMLElement): void {
    if (target.offsetHeight + Math.ceil(target.scrollTop) >= target.scrollHeight) {
      if (this.notifications.length < this.countItemsNotification && !this.loading && !this.paginationLoading) {
        this.paginationLoading = true;
        this.queryParams.offset += this.queryParams.limit;
        this.loadNotifications({ concat: true });
      }
    }
  }

  public toNotification(notification: any): ActivityFeedInformerItemFindInterface {
    return notification as ActivityFeedInformerItemFindInterface;
  }
}
